import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';
import { WeeklyScheduleState } from '@/sections/attacks/capabilities/components/WeeklySchedule';

export interface CapabilitySchedule {
  scheduleId: string;
  key: string;
  username: string;
  capabilityName: string;
  clientId?: string; // Optional - only present for internal/Aegis capabilities
  targetKey: string;
  config: Record<string, string>; // Includes credentials (Username, Password, Domain, etc.)
  weeklySchedule: WeeklyScheduleState;
  startDate: string;
  endDate?: string;
  status: 'active' | 'paused' | 'expired';
  nextExecution?: string;
  lastExecution?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateScheduleRequest {
  capabilityName: string;
  clientId?: string; // Optional - only required for internal/Aegis capabilities
  targetKey: string;
  config: Record<string, string>;
  weeklySchedule: WeeklyScheduleState;
  startDate: string;
  endDate?: string;
}

export interface UpdateScheduleRequest {
  weeklySchedule?: WeeklyScheduleState;
  startDate?: string;
  endDate?: string;
  config?: Record<string, string>;
}

/**
 * Hook to create a new capability schedule
 */
export function useCreateCapabilitySchedule() {
  const axios = useAxios();

  return useMutation({
    mutationFn: async (request: CreateScheduleRequest) => {
      const response = await axios.post<CapabilitySchedule>(
        '/capability/schedule',
        request
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['capability', 'schedules'] });
      toast.success('Schedule created successfully');
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Failed to create schedule';
      toast.error(message);
    },
  });
}

/**
 * Response format from /my endpoint for capability_schedule queries
 */
interface MyCapabilityScheduleResponse {
  capabilityschedules: CapabilitySchedule[];
  count: number;
}

/**
 * Hook to list all capability schedules for the current user
 * Uses the /my endpoint with key=#capability_schedule# to query all schedules
 * Note: Endpoint name will be updated in backend later
 */
export function useListCapabilitySchedules() {
  const axios = useAxios();

  return useQuery({
    queryKey: ['capability', 'schedules'],
    queryFn: async () => {
      const response = await axios.get<MyCapabilityScheduleResponse>('/my', {
        params: {
          key: '#capability_schedule#',
        },
      });
      // Extract the capabilityschedules array from the response
      return response.data.capabilityschedules || [];
    },
  });
}

/**
 * Hook to update a capability schedule
 */
export function useUpdateCapabilitySchedule() {
  const axios = useAxios();

  return useMutation({
    mutationFn: async ({
      scheduleId,
      updates,
    }: {
      scheduleId: string;
      updates: UpdateScheduleRequest;
    }) => {
      const response = await axios.put<CapabilitySchedule>(
        `/capability/schedule/${scheduleId}`,
        updates
      );
      return response.data;
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['capability', 'schedules'] });
      queryClient.invalidateQueries({
        queryKey: ['capability', 'schedules', data.scheduleId],
      });
      toast.success('Schedule updated successfully');
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Failed to update schedule';
      toast.error(message);
    },
  });
}

/**
 * Hook to delete a capability schedule
 */
export function useDeleteCapabilitySchedule() {
  const axios = useAxios();

  return useMutation({
    mutationFn: async (scheduleId: string) => {
      await axios.delete(`/capability/schedule/${scheduleId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['capability', 'schedules'] });
      toast.success('Schedule deleted successfully');
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Failed to delete schedule';
      toast.error(message);
    },
  });
}

/**
 * Hook to pause a capability schedule
 */
export function usePauseCapabilitySchedule() {
  const axios = useAxios();

  return useMutation({
    mutationFn: async (scheduleId: string) => {
      const response = await axios.patch<CapabilitySchedule>(
        `/capability/schedule/${scheduleId}/pause`
      );
      return response.data;
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['capability', 'schedules'] });
      queryClient.invalidateQueries({
        queryKey: ['capability', 'schedules', data.scheduleId],
      });
      toast.success('Schedule paused');
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Failed to pause schedule';
      toast.error(message);
    },
  });
}

/**
 * Hook to resume a paused capability schedule
 */
export function useResumeCapabilitySchedule() {
  const axios = useAxios();

  return useMutation({
    mutationFn: async (scheduleId: string) => {
      const response = await axios.patch<CapabilitySchedule>(
        `/capability/schedule/${scheduleId}/resume`
      );
      return response.data;
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['capability', 'schedules'] });
      queryClient.invalidateQueries({
        queryKey: ['capability', 'schedules', data.scheduleId],
      });
      toast.success('Schedule resumed');
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Failed to resume schedule';
      toast.error(message);
    },
  });
}
